import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';

import { formatDateTime, postponeBaseMs } from './pendencies-adapter';

import type { PendencyWireItem } from '../../lib/api-client';
import type { JSX } from 'react';

/** Limite padrão de adiamentos (Regulated = ilimitado; o 422 do backend manda — D5). */
const POSTPONE_LIMIT = 3;

const PRESETS: { id: string; label: string; hours: number }[] = [
  { id: '4h', label: '+ 4 horas', hours: 4 },
  { id: '1d', label: '+ 1 dia', hours: 24 },
  { id: '3d', label: '+ 3 dias', hours: 72 },
  { id: '7d', label: '+ 7 dias', hours: 168 },
];

/**
 * PostponePendencyModal — adia a pendência (Brief 2B §8.8). Idioma de modal do
 * atendente (mesmo do PendencyCreateModal): overlay fixo + DS `btn`. Razão
 * obrigatória; contador/limite honesto (D5) — não bloqueia no FE.
 *
 * O preset soma sobre `postponeBaseMs` (prazo atual, se futuro; senão agora) e
 * o novo prazo resultante é mostrado ANTES de confirmar (N2-12).
 */
export function PostponePendencyModal({
  open,
  pendency,
  submitting,
  onClose,
  onConfirm,
}: {
  open: boolean;
  pendency: PendencyWireItem;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (p: { newDueAt: string; reason: string }) => void;
}): JSX.Element | null {
  const [presetId, setPresetId] = useState('1d');
  const [reason, setReason] = useState('');

  if (!open) return null;

  const canConfirm = reason.trim().length > 0 && !submitting;
  const reachedLimit = pendency.postponeCount + 1 >= POSTPONE_LIMIT;

  const hours = PRESETS.find((p) => p.id === presetId)?.hours ?? 24;
  const nowMs = Date.now();
  const baseMs = postponeBaseMs(pendency.deadline, nowMs);
  const newDueAt = new Date(baseMs + hours * 60 * 60 * 1000).toISOString();
  const baseIsDeadline = pendency.deadline !== null && baseMs !== nowMs;

  const handleConfirm = (): void => {
    onConfirm({ newDueAt, reason: reason.trim() });
  };

  return (
    <div
      role="dialog"
      aria-label="Adiar pendência"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="w-full max-w-lg rounded-lg p-6 shadow-xl"
        style={{ background: 'var(--bg-surface)' }}
      >
        <h2 className="mb-1 text-lg font-semibold">Adiar pendência</h2>
        <p className="mb-4 text-sm" style={{ color: 'var(--fg-3)' }}>
          {pendency.title}
        </p>

        <div className="mb-3 text-sm">
          <span style={{ color: 'var(--fg-3)' }}>Adiamentos: </span>
          <span className="mono">
            {pendency.postponeCount} / {POSTPONE_LIMIT}
          </span>
        </div>

        <div className="mb-3">
          <span className="mb-1 block text-sm font-medium">Adiar por</span>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`btn btn--sm ${presetId === p.id ? 'btn--primary' : 'btn--secondary'}`}
                onClick={() => setPresetId(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-sm" style={{ color: 'var(--fg-3)' }}>
            Novo prazo: <span className="mono font-medium">{formatDateTime(newDueAt)}</span>
            {' · '}
            {baseIsDeadline ? 'contado a partir do prazo atual' : 'contado a partir de agora'}
          </p>
        </div>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Motivo (obrigatório)</span>
          <textarea
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motivo do adiamento (ex.: cliente pediu mais prazo para o documento)"
          />
        </label>

        {reachedLimit && (
          <div
            className="mb-3 flex items-start gap-2 rounded p-2 text-sm"
            style={{
              color: 'var(--warning-fg)',
              background: 'var(--warning-bg)',
              border: '1px solid var(--warning-border)',
            }}
          >
            <AlertTriangle size={14} aria-hidden />
            <span>
              No limite de adiamentos: a pendência poderá ser escalada ao supervisor após este
              adiamento.
            </span>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border px-4 py-2 text-sm"
            style={{ borderColor: 'var(--border-default)' }}
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {submitting ? 'Adiando…' : 'Confirmar adiamento'}
          </button>
        </div>
      </div>
    </div>
  );
}
